import { intl, mathService, numberHelpers } from '@approvalmax/utils';
import { useAtomValue } from 'jotai';
import { selectors } from 'modules/common';
import { domain } from 'modules/data';
import { useDispatch } from 'modules/react-redux';
import moment from 'moment';
import { reloadRequestList } from 'pages/requestList/actions';
import { useCallback, useMemo, useState } from 'react';
import { netSuiteSourceMatchingLineState, netSuiteTargetMatchingLineState } from 'shared/components/(matchings)';
import { GetNetSuiteExchangeRates, useGetNetSuiteExchangeRates } from 'shared/data/webApp/v1';
import { findDocumentFieldState } from 'shared/helpers';
import { useNetSuiteCreateMatching } from 'shared/hooks/(matchings)';

export const useNetSuiteRequestNetAmount = (details: domain.NetSuiteRequestDetails) => {
    switch (details.integrationCode) {
        case domain.IntegrationCode.NetSuiteBill:
        case domain.IntegrationCode.NetSuitePO:
        case domain.IntegrationCode.NetSuiteVRA:
            return details.netAmount;

        case domain.IntegrationCode.NetSuiteExpenseReport:
        case domain.IntegrationCode.NetSuiteSalesOrder:
        case domain.IntegrationCode.NetSuiteJournalEntry:
        default:
            return null;
    }
};

export const getSalesOrderFieldSettings = (documentFields: domain.DocumentField[] | undefined) => {
    return {
        billingSchedule: findDocumentFieldState(documentFields, 'BillingSchedule'),
        class: findDocumentFieldState(documentFields, 'Class'),
        department: findDocumentFieldState(documentFields, 'Department'),
        discountItem: findDocumentFieldState(documentFields, 'DiscountItem'),
        discountRate: findDocumentFieldState(documentFields, 'DiscountRate'),
        endDate: findDocumentFieldState(documentFields, 'EndDate'),
        location: findDocumentFieldState(documentFields, 'Location'),
        memo: findDocumentFieldState(documentFields, 'Memo'),
        paymentReference: findDocumentFieldState(documentFields, 'PaymentReference'),
        startDate: findDocumentFieldState(documentFields, 'StartDate'),
        terms: findDocumentFieldState(documentFields, 'Terms'),
        transactionDate: findDocumentFieldState(documentFields, 'TransactionDate'),
        transactionNumber: findDocumentFieldState(documentFields, 'TransactionNumber'),
        vatRegistrationNumber: findDocumentFieldState(documentFields, 'VatRegistrationNumber'),
    };
};

export const useVRAFieldSettings = (documentFields: domain.DocumentField[] | undefined) => {
    return {
        vendor: findDocumentFieldState(documentFields, 'Vendor'),
        billingAddress: findDocumentFieldState(documentFields, 'BillingAddress'),
        transactionNumber: findDocumentFieldState(documentFields, 'TransactionNumber'),
        currency: findDocumentFieldState(documentFields, 'Currency'),
        exchangeRate: findDocumentFieldState(documentFields, 'OriginalExchangeRate'),
        transactionDate: findDocumentFieldState(documentFields, 'TransactionDate'),
        class: findDocumentFieldState(documentFields, 'Class'),
        department: findDocumentFieldState(documentFields, 'Department'),
        location: findDocumentFieldState(documentFields, 'Location'),
        memo: findDocumentFieldState(documentFields, 'Memo'),
    };
};

export const getSalesOrderItemsFieldSettings = (documentFields: domain.DocumentField[] | undefined) => {
    return {
        amount: findDocumentFieldState(documentFields, 'ItemsAmount'),
        grossAmount: findDocumentFieldState(documentFields, 'ItemsGrossAmount'),
        class: findDocumentFieldState(documentFields, 'ItemsClass'),
        department: findDocumentFieldState(documentFields, 'ItemsDepartment'),
        description: findDocumentFieldState(documentFields, 'ItemsDescription'),
        excludeFromRateRequest: findDocumentFieldState(documentFields, 'ItemsExcludeFromRateRequest'),
        isClosed: findDocumentFieldState(documentFields, 'ItemsIsClosed'),
        item: findDocumentFieldState(documentFields, 'ItemsItem'),
        location: findDocumentFieldState(documentFields, 'ItemsLocation'),
        orderPriority: findDocumentFieldState(documentFields, 'ItemsOrderPriority'),
        priceLevel: findDocumentFieldState(documentFields, 'ItemsPriceLevel'),
        pst: findDocumentFieldState(documentFields, 'ItemsPST'),
        pstAmount: findDocumentFieldState(documentFields, 'ItemsPSTAmount'),
        quantity: findDocumentFieldState(documentFields, 'ItemsQuantity'),
        quantityBackOrdered: findDocumentFieldState(documentFields, 'ItemsQuantityBackOrdered'),
        quantityBilled: findDocumentFieldState(documentFields, 'ItemsQuantityBilled'),
        quantityCommitted: findDocumentFieldState(documentFields, 'ItemsQuantityCommitted'),
        quantityFulfilled: findDocumentFieldState(documentFields, 'ItemsQuantityFulfilled'),
        revenueRecognitionEndDate: findDocumentFieldState(documentFields, 'ItemsRevenueRecognitionEndDate'),
        revenueRecognitionSchedule: findDocumentFieldState(documentFields, 'ItemsRevenueRecognitionSchedule'),
        revenueRecognitionStartDate: findDocumentFieldState(documentFields, 'ItemsRevenueRecognitionStartDate'),
        serialNumbers: findDocumentFieldState(documentFields, 'ItemsSerialNumbers'),
        taxAmount: findDocumentFieldState(documentFields, 'ItemsTaxAmount'),
        taxCode: findDocumentFieldState(documentFields, 'ItemsTaxCode'),
        taxRate: findDocumentFieldState(documentFields, 'ItemsTaxRate'),
        unitPrice: findDocumentFieldState(documentFields, 'ItemsUnitPrice'),
        units: findDocumentFieldState(documentFields, 'ItemsUnits'),
    };
};

export const useJournalEntryFieldSettings = (documentFields: domain.DocumentField[] | undefined) => {
    return {
        reference: findDocumentFieldState(documentFields, 'Reference'),
        currency: findDocumentFieldState(documentFields, 'Currency'),
        exchangeRate: findDocumentFieldState(documentFields, 'ExchangeRate'),
        transactionNumber: findDocumentFieldState(documentFields, 'TransactionNumber'),
        transactionDate: findDocumentFieldState(documentFields, 'TransactionDate'),
        reversalDate: findDocumentFieldState(documentFields, 'ReversalDate'),
        reversalDefer: findDocumentFieldState(documentFields, 'ReversalDefer'),
        memo: findDocumentFieldState(documentFields, 'Memo'),
        department: findDocumentFieldState(documentFields, 'Department'),
        class: findDocumentFieldState(documentFields, 'Class'),
        location: findDocumentFieldState(documentFields, 'Location'),
        postingPeriod: findDocumentFieldState(documentFields, 'PostingPeriod'),
        reversal: findDocumentFieldState(documentFields, 'Reversal'),
    };
};

export const useJournalEntryLinesFieldSettings = (documentFields: domain.DocumentField[] | undefined) => {
    return {
        item: findDocumentFieldState(documentFields, 'ItemsItem'),
        account: findDocumentFieldState(documentFields, 'ItemsAccount'),
        taxAccount: findDocumentFieldState(documentFields, 'ItemsTaxAccount'),
        creditAmount: findDocumentFieldState(documentFields, 'ItemsCreditAmount'),
        debitAmount: findDocumentFieldState(documentFields, 'ItemsDebitAmount'),
        grossAmount: findDocumentFieldState(documentFields, 'ItemsGrosstAmount'),
        taxCode: findDocumentFieldState(documentFields, 'ItemsTaxCode'),
        taxRate: findDocumentFieldState(documentFields, 'ItemsTaxRate'),
        taxAmount: findDocumentFieldState(documentFields, 'ItemsTaxAmount'),
        memo: findDocumentFieldState(documentFields, 'ItemsMemo'),
        department: findDocumentFieldState(documentFields, 'ItemsDepartment'),
        class: findDocumentFieldState(documentFields, 'ItemsClass'),
        location: findDocumentFieldState(documentFields, 'ItemsLocation'),
        entity: findDocumentFieldState(documentFields, 'ItemsEntity'),
        amortizationSchedule: findDocumentFieldState(documentFields, 'ItemsAmortizationSchedule'),
        amortizationStartDate: findDocumentFieldState(documentFields, 'ItemsAmortizationStartDate'),
        amortizationEndDate: findDocumentFieldState(documentFields, 'ItemsAmortizationEndDate'),
        amortizationResidual: findDocumentFieldState(documentFields, 'ItemsAmortizationResidual'),
    };
};

export const useDisplayCurrency = () => {
    const [displayCurrency, setDisplayCurrency] = useState<
        GetNetSuiteExchangeRates['response']['rates'][number] | null
    >(null);

    const onDisplayCurrencyChange = useCallback((value: GetNetSuiteExchangeRates['response']['rates'][number]) => {
        setDisplayCurrency(value);
    }, []);

    return {
        displayCurrency,
        onDisplayCurrencyChange,
    };
};

export const useGetExchangeInfo = (
    companyId: string,
    requestExchangeRate: number | null,
    displayCurrencyId?: string
) => {
    const { data: exchangeRatesData } = useGetNetSuiteExchangeRates(
        { query: { companyId, forDate: moment().startOf('day').toISOString() } },
        { refetchOnMount: false, enabled: false }
    );

    const exchangeRate = useMemo(() => {
        const selectedExchangeRate = exchangeRatesData?.rates.find(
            (exchangeRate) => exchangeRate.id === displayCurrencyId
        );

        return requestExchangeRate && selectedExchangeRate
            ? mathService.divide(requestExchangeRate, selectedExchangeRate.rateValue)
            : 1;
    }, [displayCurrencyId, exchangeRatesData?.rates, requestExchangeRate]);

    return {
        exchangeRate,
    };
};

export const useDiscountRate = (discountRate: number | null, isDiscountRateAbsolute: boolean | null) => {
    return useMemo(() => {
        if (!numberHelpers.isNumber(discountRate)) {
            return null;
        }

        const rate = intl.formatNumber(discountRate, 'auto');

        return isDiscountRateAbsolute ? rate : `${rate}%`;
    }, [discountRate, isDiscountRateAbsolute]);
};

export const useOnSuccessDeleteMatching = () => {
    const dispatch = useDispatch();

    return useCallback(() => {
        dispatch(reloadRequestList());
    }, [dispatch]);
};

export const useSaveMatching = (request: selectors.types.NetSuiteExpandedRequest) => {
    const dispatch = useDispatch();
    const sourceLine = useAtomValue(netSuiteSourceMatchingLineState);
    const targetLine = useAtomValue(netSuiteTargetMatchingLineState);

    const { createMatching } = useNetSuiteCreateMatching(sourceLine?.lineType);

    const saveMatching = useCallback(
        (onClose: VoidFunction) => {
            createMatching(
                {
                    params: {
                        path: {
                            companyId: request.companyId,
                            sourceRequestId: request.id,
                            sourceLineNumber: sourceLine?.lineNumber,
                            targetRequestId: targetLine?.requestId,
                            targetLineNumber: targetLine?.lineNumber,
                        },
                    },
                },
                {
                    onSuccess: () => {
                        dispatch(reloadRequestList());
                        onClose();
                    },
                }
            );
        },
        [request.companyId, request.id, sourceLine, targetLine, createMatching, dispatch]
    );

    return {
        saveMatching,
    };
};
